export const layerGroupIds = [
  'bike-region',
  'planned-bike-connections',
  'planned-cycling-connections',
  'bike-trails',
  'zugerwald-routes',
  'zugerbergbahn',
  'recreation-network',
  'daily-network',
  'cycling-highway',
] as const;

export type LayerGroupId = (typeof layerGroupIds)[number];
export type LayerVisibilityState = Readonly<Record<LayerGroupId, boolean>>;

export type MapContentSourceId =
  | 'canonical-trails'
  | 'cantons'
  | 'cycling-bike'
  | 'cycling-daily'
  | 'cycling-highway'
  | 'cycling-planned-bike'
  | 'cycling-planned-other'
  | 'cycling-route'
  | 'riding-areas'
  | 'zugerbergbahn'
  | 'zugerwald-vector';

export type RenderingResponsibility =
  | 'area-fill'
  | 'area-label'
  | 'area-outline'
  | 'line-casing'
  | 'network-line'
  | 'route-label'
  | 'station-label'
  | 'station-marker'
  | 'trail-hit-target'
  | 'trail-line'
  | 'trail-selection';

export interface SemanticLayerPart {
  readonly id: string;
  readonly source: MapContentSourceId;
  readonly responsibility: RenderingResponsibility;
  readonly renderOrder: number;
  readonly interactionRole?: 'hit-target' | 'visible';
}

export interface LayerGroupDefinition {
  readonly id: LayerGroupId;
  readonly labelMessageId: `layers.${LayerGroupId}`;
  readonly defaultVisible: boolean;
  readonly parts: readonly SemanticLayerPart[];
  readonly attributionIds: readonly AttributionId[];
  readonly interactionPair?: {
    readonly visible: string;
    readonly hitTarget: string;
  };
}

export type AttributionId =
  | 'esri'
  | 'gis-canton-zug'
  | 'mapzen-terrain'
  | 'openfreemap'
  | 'openmaptiles'
  | 'openstreetmap'
  | 'swisstopo'
  | 'trailforks'
  | 'zugerwald';

export interface AttributionDefinition {
  readonly id: AttributionId;
  readonly label: string;
  readonly url: string;
}

export type BasemapId = 'swisstopo' | 'satellite' | 'streets';

export interface BasemapDefinition {
  readonly id: BasemapId;
  readonly labelMessageId: `basemaps.${BasemapId}`;
  readonly switchMessageId:
    'basemaps.switchToSatellite' | 'basemaps.switchToStreets' | 'basemaps.switchToSwisstopo';
  readonly default: boolean;
  readonly attributionIds: readonly AttributionId[];
  readonly responsibilities: readonly ('background' | 'forest-overlay' | 'hillshade')[];
}

export const attributions = Object.freeze([
  { id: 'swisstopo', label: 'swisstopo', url: 'https://www.swisstopo.admin.ch/' },
  { id: 'esri', label: 'Esri', url: 'https://www.esri.com/' },
  {
    id: 'gis-canton-zug',
    label: 'Quelle: GIS Kanton Zug',
    url: 'https://zg.ch/de/planen-bauen/geoinformation/geoinformationen-nutzen/nutzungsbedingungen',
  },
  { id: 'openfreemap', label: 'OpenFreeMap', url: 'https://openfreemap.org/' },
  { id: 'openmaptiles', label: 'OpenMapTiles', url: 'https://openmaptiles.org/' },
  {
    id: 'openstreetmap',
    label: 'OpenStreetMap contributors',
    url: 'https://www.openstreetmap.org/copyright',
  },
  {
    id: 'mapzen-terrain',
    label: 'Mapzen terrain data contributors',
    url: 'https://github.com/tilezen/joerd/blob/master/docs/attribution.md',
  },
  { id: 'trailforks', label: 'Trailforks', url: 'https://www.trailforks.com/region/zug-kanton/' },
  { id: 'zugerwald', label: 'Zugerwald', url: 'https://www.zugerwald.ch/' },
] satisfies readonly AttributionDefinition[]);

export const basemapDefinitions = Object.freeze([
  {
    id: 'swisstopo',
    labelMessageId: 'basemaps.swisstopo',
    switchMessageId: 'basemaps.switchToSwisstopo',
    default: true,
    attributionIds: ['swisstopo', 'mapzen-terrain'],
    responsibilities: ['background', 'forest-overlay', 'hillshade'],
  },
  {
    id: 'satellite',
    labelMessageId: 'basemaps.satellite',
    switchMessageId: 'basemaps.switchToSatellite',
    default: false,
    attributionIds: ['esri', 'mapzen-terrain'],
    responsibilities: ['background'],
  },
  {
    id: 'streets',
    labelMessageId: 'basemaps.streets',
    switchMessageId: 'basemaps.switchToStreets',
    default: false,
    attributionIds: ['openfreemap', 'openmaptiles', 'openstreetmap', 'mapzen-terrain'],
    responsibilities: ['background', 'hillshade'],
  },
] satisfies readonly BasemapDefinition[]);

function basemapDefinition(id: BasemapId): BasemapDefinition {
  const definition = basemapDefinitions.find((candidate) => candidate.id === id);
  if (definition === undefined) throw new TypeError(`unknown basemap: ${id}`);
  return definition;
}

export function defaultBasemap(): BasemapDefinition {
  const definition = basemapDefinitions.find(({ default: isDefault }) => isDefault);
  if (definition === undefined) throw new TypeError('one basemap must be the default');
  return definition;
}

export function nextBasemap(current: BasemapId): BasemapDefinition {
  const index = basemapDefinitions.findIndex(({ id }) => id === current);
  if (index < 0) throw new TypeError(`unknown basemap: ${current}`);
  return basemapDefinitions[(index + 1) % basemapDefinitions.length] ?? defaultBasemap();
}

export function basemapAttributions(id: BasemapId): readonly AttributionDefinition[] {
  const attributionIds = new Set(basemapDefinition(id).attributionIds);
  return Object.freeze(attributions.filter((attribution) => attributionIds.has(attribution.id)));
}

export function mapAttributions(id: BasemapId): readonly AttributionDefinition[] {
  const attributionIds = new Set([...basemapDefinition(id).attributionIds, 'openstreetmap']);
  return Object.freeze(attributions.filter((attribution) => attributionIds.has(attribution.id)));
}

export const layerDefinitions = Object.freeze([
  {
    id: 'bike-region',
    labelMessageId: 'layers.bike-region',
    defaultVisible: false,
    attributionIds: ['trailforks'],
    parts: [
      {
        id: 'bike-region-fill',
        source: 'riding-areas',
        responsibility: 'area-fill',
        renderOrder: 92,
      },
      {
        id: 'bike-region-outline',
        source: 'riding-areas',
        responsibility: 'area-outline',
        renderOrder: 93,
      },
      {
        id: 'bike-region-label',
        source: 'riding-areas',
        responsibility: 'area-label',
        renderOrder: 94,
      },
    ],
  },
  {
    id: 'planned-bike-connections',
    labelMessageId: 'layers.planned-bike-connections',
    defaultVisible: false,
    attributionIds: ['gis-canton-zug'],
    parts: [
      {
        id: 'planned-bike-casing',
        source: 'cycling-planned-bike',
        responsibility: 'line-casing',
        renderOrder: 61,
      },
      {
        id: 'planned-bike-line',
        source: 'cycling-planned-bike',
        responsibility: 'network-line',
        renderOrder: 62,
      },
    ],
  },
  {
    id: 'planned-cycling-connections',
    labelMessageId: 'layers.planned-cycling-connections',
    defaultVisible: false,
    attributionIds: ['gis-canton-zug'],
    parts: [
      {
        id: 'planned-cycling-casing',
        source: 'cycling-planned-other',
        responsibility: 'line-casing',
        renderOrder: 59,
      },
      {
        id: 'planned-cycling-line',
        source: 'cycling-planned-other',
        responsibility: 'network-line',
        renderOrder: 60,
      },
    ],
  },
  {
    id: 'bike-trails',
    labelMessageId: 'layers.bike-trails',
    defaultVisible: true,
    attributionIds: ['trailforks'],
    interactionPair: { visible: 'trail-visible', hitTarget: 'trail-hit-target' },
    parts: [
      {
        id: 'trail-hit-target',
        source: 'canonical-trails',
        responsibility: 'trail-hit-target',
        renderOrder: 89,
        interactionRole: 'hit-target',
      },
      {
        id: 'trail-visible',
        source: 'canonical-trails',
        responsibility: 'trail-line',
        renderOrder: 90,
        interactionRole: 'visible',
      },
      {
        id: 'trail-selection',
        source: 'canonical-trails',
        responsibility: 'trail-selection',
        renderOrder: 91,
      },
    ],
  },
  {
    id: 'zugerwald-routes',
    labelMessageId: 'layers.zugerwald-routes',
    defaultVisible: true,
    attributionIds: ['zugerwald'],
    parts: [
      {
        id: 'zugerwald-forest-road',
        source: 'zugerwald-vector',
        responsibility: 'network-line',
        renderOrder: 70,
      },
      {
        id: 'zugerwald-bike-route',
        source: 'zugerwald-vector',
        responsibility: 'network-line',
        renderOrder: 71,
      },
    ],
  },
  {
    id: 'zugerbergbahn',
    labelMessageId: 'layers.zugerbergbahn',
    defaultVisible: true,
    attributionIds: ['openstreetmap'],
    parts: [
      {
        id: 'zugerbergbahn-casing',
        source: 'zugerbergbahn',
        responsibility: 'line-casing',
        renderOrder: 72,
      },
      {
        id: 'zugerbergbahn-route',
        source: 'zugerbergbahn',
        responsibility: 'network-line',
        renderOrder: 73,
      },
      {
        id: 'zugerbergbahn-route-label',
        source: 'zugerbergbahn',
        responsibility: 'route-label',
        renderOrder: 74,
      },
      {
        id: 'zugerbergbahn-stations',
        source: 'zugerbergbahn',
        responsibility: 'station-marker',
        renderOrder: 75,
      },
      {
        id: 'zugerbergbahn-station-labels',
        source: 'zugerbergbahn',
        responsibility: 'station-label',
        renderOrder: 76,
      },
    ],
  },
  {
    id: 'recreation-network',
    labelMessageId: 'layers.recreation-network',
    defaultVisible: true,
    attributionIds: ['gis-canton-zug'],
    parts: [
      {
        id: 'recreation-bike',
        source: 'cycling-bike',
        responsibility: 'network-line',
        renderOrder: 50,
      },
      {
        id: 'recreation-route',
        source: 'cycling-route',
        responsibility: 'network-line',
        renderOrder: 51,
      },
    ],
  },
  {
    id: 'daily-network',
    labelMessageId: 'layers.daily-network',
    defaultVisible: true,
    attributionIds: ['gis-canton-zug'],
    parts: [
      {
        id: 'daily-network',
        source: 'cycling-daily',
        responsibility: 'network-line',
        renderOrder: 40,
      },
    ],
  },
  {
    id: 'cycling-highway',
    labelMessageId: 'layers.cycling-highway',
    defaultVisible: true,
    attributionIds: ['gis-canton-zug'],
    parts: [
      {
        id: 'cycling-highway',
        source: 'cycling-highway',
        responsibility: 'network-line',
        renderOrder: 41,
      },
    ],
  },
] satisfies readonly LayerGroupDefinition[]);

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function createLayerVisibilityState(values: unknown): LayerVisibilityState {
  if (!isRecord(values)) {
    throw new TypeError('layer visibility state must be an object');
  }
  const suppliedIds = Object.keys(values).sort();
  const expectedIds = [...layerGroupIds].sort();
  if (JSON.stringify(suppliedIds) !== JSON.stringify(expectedIds)) {
    throw new TypeError('layer visibility state must contain every known group exactly once');
  }
  const state = Object.fromEntries(
    layerGroupIds.map((id) => {
      const value = values[id];
      if (typeof value !== 'boolean') {
        throw new TypeError(`layer visibility for ${id} must be boolean`);
      }
      return [id, value];
    }),
  ) as Record<LayerGroupId, boolean>;
  return Object.freeze(state);
}

export const defaultLayerVisibility = createLayerVisibilityState(
  Object.fromEntries(
    layerDefinitions.map((definition) => [definition.id, definition.defaultVisible]),
  ),
);
